import React, { useEffect, useState } from "react";
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, IconButton, MenuItem, Paper, Select, Tab, Tabs,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography, Alert,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import PublishIcon from "@mui/icons-material/Publish";
import ArchiveIcon from "@mui/icons-material/Archive";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { RecommendationsService } from "services/recommendations";
import { MemberPicker } from "components/MemberPicker";

const MODULES = ["finance", "tax", "dte", "payroll", "docs", "general"];
const PRIORITIES = ["low", "medium", "high"];
const STATUS_COLORS = { DRAFT: "default", PUBLISHED: "success", ARCHIVED: "warning" };
const PRIORITY_COLORS = { low: "default", medium: "warning", high: "error" };

function RecForm({ initial = {}, templates = [], onChange }) {
  const [form, setForm] = useState({
    title: initial.title || "",
    body: initial.body || "",
    module: initial.module || "general",
    priority: initial.priority || "medium",
  });
  useEffect(() => { onChange(form); }, [form]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
      {templates.length > 0 && (
        <Box>
          <Typography variant="caption" color="text.secondary">Usar plantilla (opcional):</Typography>
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 0.5 }}>
            {templates.slice(0, 6).map((t) => (
              <Chip
                key={t._id}
                size="small"
                label={t.title}
                onClick={() => setForm({ title: t.title, body: t.body, module: t.module, priority: t.priority })}
              />
            ))}
          </Box>
        </Box>
      )}
      <TextField label="Título" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} fullWidth size="small" />
      <TextField label="Contenido (markdown OK)" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} multiline rows={6} fullWidth size="small" />
      <Box sx={{ display: "flex", gap: 2 }}>
        <Select size="small" value={form.module} onChange={(e) => setForm({ ...form, module: e.target.value })} sx={{ minWidth: 140 }}>
          {MODULES.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
        </Select>
        <Select size="small" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} sx={{ minWidth: 140 }}>
          {PRIORITIES.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
        </Select>
      </Box>
    </Box>
  );
}

function RecommendationsPanel({ memberId }) {
  const [items, setItems] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({});
  const [statusFilter, setStatusFilter] = useState("");

  const load = async () => {
    if (!memberId) return;
    setLoading(true);
    try {
      const res = await RecommendationsService.list(memberId, { status: statusFilter || undefined });
      setItems(res.items || []);
    } catch (e) {
      setFeedback({ kind: "error", msg: e?.response?.data?.error || e.message });
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      const res = await RecommendationsService.listTemplates();
      setTemplates(res.items || []);
    } catch (e) {
      // silent
    }
  };

  useEffect(() => { load(); loadTemplates(); /* eslint-disable-next-line */ }, [memberId, statusFilter]);

  const handleCreate = async () => {
    if (!form.title || !form.body) {
      setFeedback({ kind: "error", msg: "Título y contenido son requeridos" });
      return;
    }
    try {
      await RecommendationsService.create(memberId, form);
      setFeedback({ kind: "ok", msg: "Recomendación creada (DRAFT)" });
      setModalOpen(false);
      await load();
    } catch (e) {
      setFeedback({ kind: "error", msg: e?.response?.data?.error || e.message });
    }
  };

  const handlePublish = async (id) => {
    try {
      await RecommendationsService.publish(memberId, id);
      setFeedback({ kind: "ok", msg: "Publicada — el cliente ya puede verla" });
      await load();
    } catch (e) {
      setFeedback({ kind: "error", msg: e?.response?.data?.error || e.message });
    }
  };

  const handleArchive = async (id) => {
    try {
      await RecommendationsService.archive(memberId, id);
      await load();
    } catch (e) {
      setFeedback({ kind: "error", msg: e?.response?.data?.error || e.message });
    }
  };

  const handleRemove = async (id) => {
    if (!window.confirm("¿Eliminar permanentemente?")) return;
    try {
      await RecommendationsService.remove(memberId, id);
      await load();
    } catch (e) {
      setFeedback({ kind: "error", msg: e?.response?.data?.error || e.message });
    }
  };

  return (
    <Box>
      {feedback && <Alert severity={feedback.kind === "ok" ? "success" : "error"} onClose={() => setFeedback(null)} sx={{ mb: 2 }}>{feedback.msg}</Alert>}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2, gap: 2, flexWrap: "wrap" }}>
        <Select size="small" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} displayEmpty sx={{ minWidth: 180 }}>
          <MenuItem value="">Todos los estados</MenuItem>
          <MenuItem value="DRAFT">Borradores</MenuItem>
          <MenuItem value="PUBLISHED">Publicadas</MenuItem>
          <MenuItem value="ARCHIVED">Archivadas</MenuItem>
        </Select>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setForm({}); setModalOpen(true); }}>
          Nueva recomendación
        </Button>
      </Box>

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Título</TableCell>
                <TableCell>Módulo</TableCell>
                <TableCell>Prioridad</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Publicada</TableCell>
                <TableCell>Leída</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 3 }}><CircularProgress size={20} /></TableCell></TableRow>
              ) : items.length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 3, color: "text.secondary" }}>Sin recomendaciones</TableCell></TableRow>
              ) : items.map((r) => (
                <TableRow key={r._id} hover>
                  <TableCell sx={{ maxWidth: 300 }}>{r.title}</TableCell>
                  <TableCell><Chip size="small" label={r.module} /></TableCell>
                  <TableCell><Chip size="small" label={r.priority} color={PRIORITY_COLORS[r.priority] || "default"} /></TableCell>
                  <TableCell><Chip size="small" label={r.status} color={STATUS_COLORS[r.status]} /></TableCell>
                  <TableCell>{r.publishedAt ? new Date(r.publishedAt).toLocaleDateString("es-CL") : "—"}</TableCell>
                  <TableCell>{r.readByClient ? <Chip size="small" label="✓" color="success" /> : "—"}</TableCell>
                  <TableCell align="right">
                    {r.status === "DRAFT" && (
                      <IconButton size="small" color="success" onClick={() => handlePublish(r._id)} title="Publicar">
                        <PublishIcon fontSize="small" />
                      </IconButton>
                    )}
                    {r.status !== "ARCHIVED" && (
                      <IconButton size="small" onClick={() => handleArchive(r._id)} title="Archivar">
                        <ArchiveIcon fontSize="small" />
                      </IconButton>
                    )}
                    <IconButton size="small" color="error" onClick={() => handleRemove(r._id)} title="Eliminar">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Nueva recomendación</DialogTitle>
        <DialogContent>
          <RecForm templates={templates} onChange={setForm} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleCreate}>Crear (DRAFT)</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function TemplatesPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const res = await RecommendationsService.listTemplates();
      setItems(res.items || []);
    } catch (e) {
      setFeedback({ kind: "error", msg: e?.response?.data?.error || e.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!form.title || !form.body) { setFeedback({ kind: "error", msg: "Título y contenido requeridos" }); return; }
    try {
      await RecommendationsService.createTemplate(form);
      setModalOpen(false);
      await load();
    } catch (e) {
      setFeedback({ kind: "error", msg: e?.response?.data?.error || e.message });
    }
  };

  const handleRemove = async (id) => {
    if (!window.confirm("¿Eliminar plantilla?")) return;
    try {
      await RecommendationsService.removeTemplate(id);
      await load();
    } catch (e) {
      setFeedback({ kind: "error", msg: e?.response?.data?.error || e.message });
    }
  };

  return (
    <Box>
      {feedback && <Alert severity={feedback.kind === "ok" ? "success" : "error"} onClose={() => setFeedback(null)} sx={{ mb: 2 }}>{feedback.msg}</Alert>}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setForm({}); setModalOpen(true); }}>
          Nueva plantilla
        </Button>
      </Box>
      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Título</TableCell>
                <TableCell>Módulo</TableCell>
                <TableCell>Prioridad</TableCell>
                <TableCell align="right">Usos</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3 }}><CircularProgress size={20} /></TableCell></TableRow>
              ) : items.length === 0 ? (
                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3, color: "text.secondary" }}>Sin plantillas</TableCell></TableRow>
              ) : items.map((t) => (
                <TableRow key={t._id} hover>
                  <TableCell>{t.title}</TableCell>
                  <TableCell><Chip size="small" label={t.module} /></TableCell>
                  <TableCell><Chip size="small" label={t.priority} color={PRIORITY_COLORS[t.priority] || "default"} /></TableCell>
                  <TableCell align="right">{t.usageCount || 0}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" color="error" onClick={() => handleRemove(t._id)}><DeleteIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>Nueva plantilla</DialogTitle>
        <DialogContent>
          <RecForm onChange={setForm} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleCreate}>Guardar plantilla</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export function RecomendacionesView() {
  const [memberId, setMemberId] = useState(null);
  const [tab, setTab] = useState(0);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3, alignItems: "center", flexWrap: "wrap", gap: 2 }}>
        <Typography variant="h5" fontWeight={700}>Recomendaciones del contador</Typography>
        {tab === 0 && <MemberPicker value={memberId} onChange={setMemberId} />}
      </Box>
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="Por cliente" />
          <Tab label="Plantillas" />
        </Tabs>
      </Box>
      {tab === 0 ? (
        !memberId ? (
          <Paper sx={{ p: 4, textAlign: "center", color: "text.secondary" }}>Selecciona un cliente</Paper>
        ) : (
          <RecommendationsPanel memberId={memberId} />
        )
      ) : (
        <TemplatesPanel />
      )}
    </Box>
  );
}
